import * as React from "react";
import { Pagination, Table } from "semantic-ui-react";
import { Button, Input, Card } from "@/components/ui";
import { BookOpen, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { OauthApplicationServices as ApiKeyServices } from "../../services/bedrock/OauthApplicationServices";
import { OauthApplication as ApiKey } from "../../services/bedrock/dtos/OauthApplication";
import { debounce } from "lodash";
import { SaveApiKeyModal } from "./SaveApiKeyModal";
import { useAccountSettings } from "@/utils/useAccountSettings";
import { WebComponents } from "@/interfaces";

interface ApiKeysProps {}

export const ApiKeys: React.FC<ApiKeysProps> = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey>();
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.API_KEYS);

  const loadData = useCallback(
    (pageFilter?: number, searchFilter?: string, sort?: string, order?: "asc" | "desc") => {
      setIsLoading(true);
      if (pageFilter !== undefined) {
        setPage(pageFilter);
      } else {
        pageFilter = page;
      }
      if (searchFilter !== undefined) {
        setSearch(searchFilter);
      } else {
        searchFilter = search;
      }
      if (sort !== undefined) {
        setSortColumn(sort);
      } else {
        sort = sortColumn;
      }
      if (order !== undefined) {
        setSortOrder(order);
      } else {
        order = sortOrder;
      }
      ApiKeyServices.getAll(searchFilter, pageFilter, undefined, sort, order)
        .then(response => {
          setApiKeys(response.content);
          setTotalPages(response.pagination.totalPages);
          setPage(response.pagination.page);
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    },
    [page, search, sortColumn, sortOrder]
  );

  useEffect(loadData, []);

  const debouncedSearch = useCallback(
    debounce((search: string) => loadData(0, search), 500),
    [loadData]
  );

  const handleSaveApiKeyModalClose = (newApiKey?: ApiKey) => {
    setShowSaveModal(false);
    setSelectedApiKey(undefined);
    if (newApiKey) {
      loadData();
    }
  };

  const editApiKey = (apiKey: ApiKey) => {
    if (!withWritePermission) return;
    setSelectedApiKey(apiKey);
    setShowSaveModal(true);
  };

  const setSort = useCallback(
    (column: string) => () => {
      var order: "asc" | "desc" = "asc";
      if (column === sortColumn) {
        order = sortOrder === "asc" ? "desc" : "asc";
      }
      loadData(undefined, undefined, column, order);
    },
    [sortColumn, sortOrder]
  );

  const getSortedValueForColumn = (column: string) => {
    if (column === sortColumn) {
      return sortOrder === "asc" ? "ascending" : "descending";
    } else {
      return undefined;
    }
  };

  const getFormattedDateTime = (date: string) => {
    return new Date(date).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
  };

  const redirectToDocumentation = () => {
    window.open("https://socialcoach.readme.io/", "_blank");
  };

  return (
    <div className="flex flex-col">
      <SaveApiKeyModal open={showSaveModal} apiKey={selectedApiKey} onClose={handleSaveApiKeyModalClose} />

      {/* API Documentation Card */}
      <Card
        onClick={() => redirectToDocumentation()}
        className="p-5 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-md hover:border-primary/20 cursor-pointer transition-all border border-gray-200 bg-gray-50/50 rounded-xl select-none"
      >
        <div className="flex items-start gap-4">
          <div className="p-2.5 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <BookOpen className="size-5 text-primary" />
          </div>
          <div className="flex flex-col gap-1.5">
            <h3 className="text-sm font-bold text-foreground m-0 flex items-center gap-1.5">
              Access API Documentation
              <ArrowUpRight className="size-3.5 text-muted-foreground" />
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed m-0 max-w-2xl">
              Integrate SocialCoach directly with your agency CRM, internal portals, or custom tools. Our developer APIs
              allow your technical teams to get performance stats, sync player information, and set up secure automated
              custom integrations.
            </p>

            {/* Example Uses Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1 pt-2 border-t border-gray-200/40">
              <div className="flex items-center gap-2 text-xs text-foreground font-semibold">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Syncing Player Information</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground font-semibold">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Pulling Stats & Performance</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground font-semibold">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Triggering CRM Automations</span>
              </div>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => redirectToDocumentation()}
          className="font-bold shrink-0 self-start md:self-center h-8"
        >
          Open Developer Docs
        </Button>
      </Card>

      <div className="flex flex-col gap-3 mt-4 mb-4">
        <h3 className="text-base font-bold text-foreground m-0">API Keys</h3>
        <div className="flex items-center gap-2">
          <Input
            className="w-auto h-8 text-xs"
            placeholder="Search keys..."
            onChange={e => debouncedSearch(e.target.value)}
          />
          <div className="grow" />
          {withWritePermission && (
            <Button size="sm" onClick={() => setShowSaveModal(true)} className="h-8 font-bold">
              Add API Key
            </Button>
          )}
        </div>
      </div>
      <Table selectable={withWritePermission} sortable celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell sorted={getSortedValueForColumn("name")} onClick={setSort("name")}>
              Name
            </Table.HeaderCell>
            <Table.HeaderCell sorted={getSortedValueForColumn("uid")} onClick={setSort("uid")}>
              Client ID
            </Table.HeaderCell>
            <Table.HeaderCell sorted={getSortedValueForColumn("created_at")} onClick={setSort("created_at")}>
              Created
            </Table.HeaderCell>
            <Table.HeaderCell sorted={getSortedValueForColumn("updated_at")} onClick={setSort("updated_at")}>
              Updated
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {apiKeys.map(apiKey => (
            <Table.Row
              key={apiKey.id}
              className={withWritePermission ? "cursor-pointer" : ""}
              disabled={isLoading}
              onClick={() => editApiKey(apiKey)}
            >
              <Table.Cell>{apiKey.name}</Table.Cell>
              <Table.Cell>{apiKey.uid}</Table.Cell>
              <Table.Cell>{getFormattedDateTime(apiKey.createdAt)}</Table.Cell>
              <Table.Cell>{getFormattedDateTime(apiKey.updatedAt)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {totalPages > 1 && (
        <Pagination
          className="self-center"
          activePage={page}
          totalPages={totalPages}
          disabled={isLoading}
          boundaryRange={3}
          pointing
          secondary
          firstItem={null}
          lastItem={null}
          onPageChange={(_, { activePage }) => loadData((activePage as number) - 1)}
        />
      )}
    </div>
  );
};
